import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import type { TabConfig } from '@/ui/tabs/tabs';
import { Tabs } from '@/ui/tabs/tabs';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import {
  defaultSettingsAcitveTab,
  settingsActiveTabState,
} from './settings.state';
import { SettingsTabContent } from './settings-tab-content';
import { SettingsTabControl } from './settings-tab-control';
import type { SettingsTabs } from './use-settings-tabs-config';
import { useSettingsTabsConfig } from './use-settings-tabs-config';
import { useUsersCount } from './use-users-count.query';
import { useSearchParams } from 'react-router-dom';
import { useAtom } from 'jotai';

type SettingsTabCounterMapper = Record<SettingsTabs, number>;

export const SettingsContent = () => {
  const { t } = useTranslation('settings');
  const { hasRequiredPermission } = useUserPermissions();

  const [activeTab, setActiveTab] = useAtom(settingsActiveTabState);
  const resetActiveTab = () => setActiveTab(defaultSettingsAcitveTab);

  const { data: usersCount } = useUsersCount();
  const [searchParams, setSearchParams] = useSearchParams();

  const settingsTabsConfig = useSettingsTabsConfig();

  // Get the default tab based on permissions
  const getDefaultTab = (): SettingsTabs => {
    if (hasRequiredPermission(PERMISSION.VIEW_GENERAL_SETTINGS)) {
      return 'general';
    }
    return 'profileSettings';
  };

  useEffect(() => {
    const tabParam = searchParams.get('tab');

    if (tabParam === 'accessTokens') {
      setActiveTab('apiAndIntegrations');
      setSearchParams({ tab: 'apiAndIntegrations' }, { replace: true });
      return;
    }

    const tab = tabParam as SettingsTabs;
    const defaultTab = getDefaultTab();

    if (tab) {
      // Check if user has permission to access the requested tab
      if (
        tab === 'general' &&
        !hasRequiredPermission(PERMISSION.VIEW_GENERAL_SETTINGS)
      ) {
        // Redirect to default tab if user doesn't have permission
        setActiveTab(defaultTab);
        setSearchParams({ tab: defaultTab });
        return;
      }
      if (
        tab === 'apiAndIntegrations' &&
        !hasRequiredPermission(PERMISSION.MANAGE_API_AND_INTEGRATIONS)
      ) {
        setActiveTab(defaultTab);
        setSearchParams({ tab: defaultTab });
        return;
      }
      setActiveTab(tab);
      return;
    }

    // Set default tab if no tab in URL
    setActiveTab(defaultTab);
    setSearchParams({ tab: defaultTab });
  }, [
    activeTab,
    searchParams,
    setActiveTab,
    setSearchParams,
    hasRequiredPermission,
  ]);

  useEffect(() => {
    return () => {
      resetActiveTab();
    };
  }, []);

  const mappedTabCounterValues: SettingsTabCounterMapper = {
    apiAndIntegrations: 0,
    feed: 0,
    general: 0,
    invitedUsers: usersCount?.invitedUsers ?? 0,
    manageTemplates: 0,
    privacy: 0,
    profileSettings: 0,
    userManagement: usersCount?.activeAndDisabledUsers ?? 0,
  };

  const tabsConfig: TabConfig<SettingsTabs>[] = settingsTabsConfig.map(tab => ({
    id: tab.id,
    title: (
      <SettingsTabControl
        id={tab.id}
        name={t(tab.id)}
        countIndicator={mappedTabCounterValues[tab.id]}
      />
    ),
    content: <SettingsTabContent tab={tab.id} />,
  }));

  return (
    <SettingsContainer>
      <Tabs<SettingsTabs>
        tabsConfig={tabsConfig}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        border
        width="max-content"
        noShadow
        padding="0px 28px"
      />
    </SettingsContainer>
  );
};
const SettingsContainer = styled('div')`
  padding: 32px 0px;
  background-color: ${({ theme }) => theme.colors.basics.white};
`;
